import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { JsonMetadataField } from '../../components/metadata/JsonMetadataField';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  MarketSegment,
  MarketSegmentType,
  MarketSegmentUpsertPayload,
  createMarketSegment,
  deleteMarketSegment,
  downloadMarketSegmentImportTemplate,
  getMarketSegmentImportStatus,
  importMarketSegments,
  listMarketSegments,
  updateMarketSegment,
} from '../../services/marketSegmentsApi';

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

const SEGMENT_TYPE_OPTIONS: { value: MarketSegmentType; label: string }[] = [
  { value: 'Industry', label: 'Indústria / setor' },
  { value: 'CompanySize', label: 'Porte da empresa' },
  { value: 'Geography', label: 'Geografia' },
  { value: 'Vertical', label: 'Vertical' },
  { value: 'Custom', label: 'Personalizado' },
];

type MarketSegmentFormState = Omit<MarketSegmentUpsertPayload, 'sortOrder'>;

const emptyForm: MarketSegmentFormState = {
  name: '',
  description: '',
  segmentType: 'Industry',
  metadataJson: '',
  isActive: true,
};

function segmentTypeLabel(value: string): string {
  return SEGMENT_TYPE_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function MarketSegmentsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.market-segments');
  const [items, setItems] = useState<MarketSegment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MarketSegmentFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listMarketSegments({
        page: targetPage,
        pageSize,
        search: search || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar segmentos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(segment: MarketSegment): void {
    expandForm();
    setEditingId(segment.id);
    setForm({
      name: segment.name,
      description: segment.description,
      segmentType: segment.segmentType,
      metadataJson: segment.metadataJson ?? '',
      isActive: segment.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: MarketSegmentUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateMarketSegment(editingId, payload);
        toast.success('Segmento atualizado.');
        resetForm();
      } else {
        await createMarketSegment(payload);
        toast.success('Segmento criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar segmento.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(segment: MarketSegment): Promise<void> {
    if (!canWrite) return;

    const accepted = await confirm({
      title: 'Excluir segmento',
      message: `Deseja excluir "${segment.name}"? Produtos vinculados perderão este vínculo.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteMarketSegment(segment.id);
      toast.success('Segmento excluído.');
      if (editingId === segment.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir segmento.');
    }
  }

  async function handleDownloadTemplate(): Promise<void> {
    try {
      const blob = await downloadMarketSegmentImportTemplate();
      triggerBlobDownload(blob, 'segmentos-mercado-template.csv');
      toast.success('Template baixado.');
    } catch {
      toast.error('Falha ao baixar template.');
    }
  }

  async function pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await getMarketSegmentImportStatus(importId);
        if (status.status === 'Completed') {
          toast.success(
            `Importação concluída: ${status.createdCount} criados, ${status.updatedCount} atualizados.`,
          );
          await loadData(page);
          return;
        }
        if (status.status === 'Failed') {
          toast.error(status.errorSummary || 'Importação falhou.');
          return;
        }
      } catch {
        // Continua tentando enquanto o job processa.
      }
    }
    toast.info('Importação ainda em andamento. Atualize a lista quando concluir.');
  }

  async function handleImportFile(file: File | undefined): Promise<void> {
    if (!file || !canWrite) return;
    setImporting(true);
    try {
      const queued = await importMarketSegments(file);
      toast.success(
        queued.hangfireJobId
          ? `Importação enfileirada (Hangfire ${queued.hangfireJobId}).`
          : 'Importação enfileirada.',
      );
      void pollImportStatus(queued.importId);
    } catch {
      toast.error('Falha ao enviar arquivo de importação.');
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  }

  const formTitle = editingId ? 'Editar segmento' : 'Novo segmento';
  const editingFormSummary = editingId
    ? `Editando ${form.name || 'segmento selecionado'} - clique para expandir`
    : undefined;

  return (
    <PageShell>
      <PageHeader
        title="Segmentos de mercado"
        description="Classificação estratégica de mercado para produtos — distinto de tags de campanha."
        actions={
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-secondary"
              onClick={() => void handleDownloadTemplate()}
            >
              Template CSV
            </button>
            {canWrite ? (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(event) => void handleImportFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="erp-btn-secondary"
                  disabled={importing}
                  onClick={() => importInputRef.current?.click()}
                >
                  {importing ? 'Importando...' : 'Importar'}
                </button>
              </>
            ) : null}
            <Link to="/sales/products" className="erp-link-back">
              ← Voltar para produtos
            </Link>
          </div>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={formTitle}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingFormSummary}
          createSummary="Clique para expandir e cadastrar um novo segmento"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome <RequiredMark />
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.segmentType ?? 'Industry'}
                  onChange={(event) =>
                    setForm({ ...form, segmentType: event.target.value as MarketSegmentType })
                  }
                >
                  {SEGMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Descrição
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.description ?? ''}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </label>
            <div className="mt-3">
              <JsonMetadataField
                label="Metadados"
                description="Informações extras sobre o segmento, como faixa de funcionários ou CNAE."
                value={form.metadataJson}
                onChange={(metadataJson) => setForm({ ...form, metadataJson })}
              />
            </div>
            <label className="erp-label-inline mt-3">
              <input
                type="checkbox"
                checked={form.isActive ?? true}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              />
              Ativo
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-md"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void loadData(1);
            }
          }}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando segmentos..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Status</th>
                {canWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 4 : 3} className="text-center text-secondary-500">
                    Nenhum segmento cadastrado.
                  </td>
                </tr>
              ) : (
                items.map((segment) => (
                  <tr key={segment.id}>
                    <td>{segment.name}</td>
                    <td>{segmentTypeLabel(segment.segmentType)}</td>
                    <td>
                      <ActiveStatusBadge active={segment.isActive} />
                    </td>
                    {canWrite ? (
                      <td className="text-right">
                        <EditIconButton onClick={() => startEdit(segment)} label="Editar" />
                        <DeleteIconButton onClick={() => void handleDelete(segment)} label="Excluir" />
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
